// src/admin/pages/tradein/tradeInService.ts
import {
  collection, addDoc, updateDoc, deleteDoc, deleteField, doc, getDocs, getDoc, query,
  orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { TradeInSubmission } from '../../../enums/tradeIn';
import { AuditService } from '../audit/auditService';

const COLLECTION = 'tradeInSubmissions';

const mapDoc = (id: string, data: any): TradeInSubmission => ({
  id,
  ownerName: data.ownerName || '',
  ownerPhone: data.ownerPhone || '',
  ownerEmail: data.ownerEmail || '',
  carMake: data.carMake || '',
  carModel: data.carModel || '',
  carYear: data.carYear || 0,
  mileageKm: data.mileageKm || 0,
  condition: data.condition || '',
  registrationNumber: data.registrationNumber || '',
  askingPrice: data.askingPrice || 0,
  description: data.description || '',
  images: data.images || [],
  status: data.status || 'New Submission',
  offeredPrice: data.offeredPrice || 0,
  internalNotes: data.internalNotes || '',
  rejectionReason: data.rejectionReason || undefined,
  convertedToCarId: data.convertedToCarId || '',
  appliedToSaleCarId: data.appliedToSaleCarId || undefined,
  createdAt: data.createdAt?.toDate?.() || new Date(),
  updatedAt: data.updatedAt?.toDate?.() || new Date(),
});

export class TradeInService {
  /** Public write from the client "Sell / Trade-In Your Car" form. */
  static async submitPublic(data: Omit<TradeInSubmission, 'id' | 'status' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const ref = await addDoc(collection(db, COLLECTION), {
      ...data,
      status: 'New Submission',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return ref.id;
  }

  static async getAll(): Promise<TradeInSubmission[]> {
    const snap = await getDocs(query(collection(db, COLLECTION), orderBy('createdAt', 'desc')));
    return snap.docs.map(d => mapDoc(d.id, d.data()));
  }

  static async update(id: string, updates: Partial<TradeInSubmission>, userId: string, userName: string): Promise<void> {
    const ref = doc(db, COLLECTION, id);
    const snap = await getDoc(ref);
    const before = snap.exists() ? snap.data() : {};
    // updateDoc() throws "Unsupported field value: undefined" if any field in
    // the payload is undefined (e.g. rejectionReason cleared when status !=
    // 'Rejected') — this Firestore instance doesn't have
    // ignoreUndefinedProperties set. Convert undefined to deleteField() so
    // the intent (clear this field) still happens instead of just crashing.
    const cleanedUpdates = Object.fromEntries(
      Object.entries(updates).map(([key, value]) => [key, value === undefined ? deleteField() : value])
    );
    await updateDoc(ref, { ...cleanedUpdates, updatedAt: serverTimestamp() } as any);
    await AuditService.log({
      action: 'status_change',
      module: 'Trade-Ins',
      entityId: id,
      entityLabel: `${before.carMake || ''} ${before.carModel || ''}`.trim() || 'Trade-in',
      description: `Updated trade-in submission from ${before.ownerName || 'customer'} (${before.carMake} ${before.carModel})${updates.status ? ` — status: ${updates.status}` : ''}${updates.rejectionReason ? ` — reason: ${updates.rejectionReason}` : ''}`,
      userId,
      userName,
    });
  }

  static async delete(id: string, userId: string, userName: string): Promise<void> {
    const ref = doc(db, COLLECTION, id);
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    await deleteDoc(ref);
    await AuditService.log({
      action: 'delete',
      module: 'Trade-Ins',
      entityId: id,
      entityLabel: `${data.carMake || ''} ${data.carModel || ''}`.trim(),
      description: `Deleted trade-in submission from ${data.ownerName || 'customer'}`,
      userId,
      userName,
    });
  }
}
